/**
 * The workspace rail (spec §8.25, M24): the page rail of §8.15 beside the page tree. Its relations are
 * workspace links between pages; optionally, links between the pages' blocks (and page-link blocks),
 * aggregated to one dimmed edge per page pair and predicate. Rows are page-tree rows, so the layout,
 * the overlay and the menu are the page rail's own.
 */
import { useQuery } from "@tanstack/react-query";
import type { LoroDoc, TreeID } from "loro-crdt";
import { useLayoutEffect, useState, type RefObject } from "react";
import { queryCacheKey, runWithCache } from "../graph/queryCache";
import { parseIri } from "../lib/iri";
import { readAllWorkspaceLinks } from "../links/model";
import type { PageRow } from "../pages/pageTree";
import type { RowGeometry } from "./layout";
import type { PageRelation } from "./model";

const KM = "https://w3id.org/kss/ns#";

/** doc id → its node in the page tree */
export function pageNodes(rows: PageRow[]): Map<string, TreeID> {
  return new Map(rows.filter((r) => r.kind === "page" && r.docId).map((r) => [r.docId!, r.treeId]));
}

const docOf = (iri: string): string | undefined => {
  const p = parseIri(iri);
  return p?.kind === "doc" ? p.doc : undefined;
};

/** Workspace links whose subject is a page; a target page outside the tree counts as elsewhere. */
export function readWorkspaceRelations(doc: LoroDoc, rows: PageRow[]): PageRelation[] {
  return relationsOf(readAllWorkspaceLinks(doc), rows);
}

export type WorkspaceLink = ReturnType<typeof readAllWorkspaceLinks>[number];

/** The page relations among `links`, placed on `rows`. Kept apart from reading the document so a
 *  selector never closes over rows from an earlier render (a cached selector would keep them). */
export function relationsOf(links: WorkspaceLink[], rows: PageRow[]): PageRelation[] {
  const nodes = pageNodes(rows);
  const out: PageRelation[] = [];
  for (const l of links) {
    const s = docOf(l.s);
    const source = s ? nodes.get(s) : undefined;
    if (!source) continue;
    if (l.o.kind !== "page" && l.o.kind !== "iri") continue;
    const o = docOf(l.o.value);
    if (!o) continue;
    out.push({ linkId: l.id, predicate: l.p, sourceTree: source, targetTree: nodes.get(o) ?? null });
  }
  return out;
}

/** Block-level links (and page-link blocks' `km:references`) between two pages, counted (§8.25). */
export const AGGREGATE_QUERY = `PREFIX km: <${KM}>
PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
SELECT ?sd ?p ?od (COUNT(*) AS ?n) WHERE {
  {
    GRAPH ?g { ?r rdf:reifies <<( ?s ?p ?o )>> }
    FILTER(STRSTARTS(STR(?g), "urn:km:graph:asserted:"))
  } UNION {
    GRAPH ?g { ?s km:references ?o }
    BIND(km:references AS ?p)
  }
  GRAPH ?g1 { ?s km:inDocument ?sd }
  { GRAPH ?g2 { ?o km:inDocument ?od } } UNION { GRAPH ?g3 { ?o a km:Document } BIND(?o AS ?od) }
  FILTER(?sd != ?od)
}
GROUP BY ?sd ?p ?od
LIMIT 2000`;

export interface AggregateRow {
  sourceDoc: string;
  predicate: string;
  targetDoc: string;
  count: number;
}

/** The aggregated source through the query cache, so it replays offline (§8.25). */
export function useAggregatedLinks(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["workspaceRailAggregate", workspaceId],
    enabled,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<AggregateRow[]> => {
      const run = await runWithCache(workspaceId, AGGREGATE_QUERY, await queryCacheKey("ws-rail", workspaceId, AGGREGATE_QUERY));
      const bindings = (run.result.results as { results?: { bindings?: Array<Record<string, { value: string } | undefined>> } }).results?.bindings ?? [];
      return bindings.flatMap((b) => {
        const sd = b.sd?.value && docOf(b.sd.value);
        const od = b.od?.value && docOf(b.od.value);
        if (!sd || !od || !b.p?.value) return [];
        return [{ sourceDoc: sd, predicate: b.p.value, targetDoc: od, count: Number(b.n?.value ?? 1) }];
      });
    },
  });
}

export function aggregateRelations(rows: AggregateRow[], tree: PageRow[]): PageRelation[] {
  const nodes = pageNodes(tree);
  return rows.flatMap((r) => {
    const source = nodes.get(r.sourceDoc);
    if (!source) return [];
    return [{ linkId: `agg:${r.sourceDoc}:${r.predicate}:${r.targetDoc}`, predicate: r.predicate, sourceTree: source, targetTree: nodes.get(r.targetDoc) ?? null, count: r.count }];
  });
}

/** A query row's endpoint — a doc IRI — as its page-tree row. */
export function iriResolver(tree: PageRow[]): (iri: string) => TreeID | undefined {
  const nodes = pageNodes(tree);
  return (iri) => {
    const d = docOf(iri);
    return d ? nodes.get(d) : undefined;
  };
}

/**
 * Row geometry from the DOM (§8.25): the page tree is not virtualised, so each visible row's
 * `offsetTop`/`offsetHeight` inside the list's positioned wrapper is exact. Re-measured after every
 * render and on resize; state changes only when a number changed.
 */
export function useDomRowGeometry(listRef: RefObject<HTMLElement | null>, rowsKey: string): { rows: RowGeometry[]; height: number } {
  const [geometry, setGeometry] = useState<{ rows: RowGeometry[]; height: number }>({ rows: [], height: 0 });
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const els = [...list.querySelectorAll<HTMLElement>("[data-page-row]")];
      const next = { rows: els.map((el) => ({ start: el.offsetTop, size: el.offsetHeight })), height: list.offsetTop + list.offsetHeight };
      setGeometry((cur) => (JSON.stringify(cur) === JSON.stringify(next) ? cur : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(list);
    return () => ro.disconnect();
  }, [listRef, rowsKey]);
  return geometry;
}

